import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Download, FileText } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useArticles } from '../articlesApi';
import { CLUSTERS, CLUSTER_ORDER, ICON_COMPONENTS, countByCluster } from '../insightsTaxonomy';

/**
 * Insights & Resources index page, long-form authority signal for AI models.
 * Sources articles from the backend (single source of truth). Newly-approved
 * articles from /admin appear here automatically without a redeploy.
 */

const V2Resources = () => {
  useSEO({
    title: 'Insights & Resources, Delivery governance, Building Safety Act, PFI hand-back, NHS capital',
    description: 'Practitioner-led long-form insights on delivery governance, the Golden Thread and Building Safety Act 2022, PFI hand-back readiness, NHS capital business cases and Tier-1 construction stage-gated frameworks.',
    path: '/insights',
  });

  const { articles, loading, error } = useArticles();
  const counts = countByCluster(articles);

  return (
    <div className="bg-white text-slate-900" data-testid="v2-resources">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-14 border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">
            Insights &amp; Resources
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            The delivery playbook,
            <span className="text-blue-600"> in plain English.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
            Practitioner-led notes on how complex delivery actually works, governance,
            evidence, regulation, and the operating models that keep NHS, PFI and Tier-1
            projects on the rails.
          </p>
        </div>
      </section>

      {/* Topic clusters strip */}
      <section className="py-10 md:py-12 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-5">
            Browse by topic
          </p>
          <div className="flex flex-wrap gap-3" data-testid="v2-resources-clusters">
            {CLUSTER_ORDER.map((key) => {
              const c = CLUSTERS[key];
              const CIcon = c.icon;
              return (
                <Link
                  key={key}
                  to={`/insights/topic/${key}`}
                  className="inline-flex items-center gap-2 border border-slate-200 hover:border-blue-500 hover:text-blue-700 bg-white text-slate-700 rounded-full px-4 py-2 text-sm transition-colors"
                  data-testid={`v2-resources-cluster-${key}`}
                >
                  <CIcon className="w-3.5 h-3.5" /> {c.label}
                  <span className="text-slate-400 text-xs">({counts[key]})</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && <p className="text-slate-500 text-center py-10">Loading articles…</p>}
          {error && !loading && (
            <div className="text-center py-10" data-testid="v2-resources-error">
              <p className="text-slate-600 mb-4">We had trouble loading the articles just now.</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 rounded-full px-5 py-2 text-sm transition-colors"
                data-testid="v2-resources-retry-btn"
              >
                Try again
              </button>
            </div>
          )}
          {!loading && !error && articles.length === 0 && (
            <p className="text-slate-500 text-center py-10">No articles yet.</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {articles.map((a) => {
              const AIcon = ICON_COMPONENTS[a.icon] || FileText;
              return (
                <Link
                  to={`/insights/${a.slug}`}
                  key={a.slug}
                  itemScope
                  itemType="https://schema.org/Article"
                  data-testid={`v2-resources-card-${a.slug}`}
                  className="group border border-slate-200 rounded-xl p-7 md:p-8 hover:border-blue-500 hover:shadow-lg transition-all bg-white block"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center">
                      <AIcon className="w-5 h-5" />
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                      {a.readMinutes} min read
                    </span>
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-blue-600 font-semibold mb-3">
                    {a.kicker}
                  </p>
                  <h2
                    itemProp="headline"
                    className="text-xl md:text-2xl font-medium text-slate-900 mb-4 leading-tight group-hover:text-blue-700 transition-colors"
                  >
                    {a.title}
                  </h2>
                  <p itemProp="abstract" className="text-[14.5px] text-slate-600 leading-relaxed mb-5">
                    {a.excerpt}
                  </p>
                  <span className="inline-flex items-center text-sm font-medium text-blue-600 group-hover:text-blue-700">
                    Read the full note
                    <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-16 border-t border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
              Downloads
            </p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">
              Practical assets you can share internally.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a
              href="/downloads/keystone-videra-client-overview.pdf"
              className="flex items-start gap-4 p-6 bg-white border border-slate-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
              data-testid="v2-resources-dl-clientoverview"
            >
              <span className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">PDF · 2 pages</p>
                <p className="text-base font-medium text-slate-900 group-hover:text-blue-700 transition-colors">Keystone &amp; Videra, Client Overview</p>
                <p className="text-sm text-slate-500 mt-1">What we do, what changes for you, and how the engagement is structured.</p>
              </div>
            </a>
            <a
              href="/downloads/keystone-videra-introduction-deck.pptx"
              className="flex items-start gap-4 p-6 bg-white border border-slate-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
              data-testid="v2-resources-dl-deck"
            >
              <span className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1">PPTX · 13 slides</p>
                <p className="text-base font-medium text-slate-900 group-hover:text-blue-700 transition-colors">Introduction slide deck</p>
                <p className="text-sm text-slate-500 mt-1">A short, avatar-narrated introduction to Keystone and the Videra platform.</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mb-4">
            Want a delivery diagnostic for your programme?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            A short call with the Keystone team, no pitch, just an honest read of where
            your delivery might be losing ground.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-slate-900 text-white px-7 py-3.5 rounded-md font-medium hover:bg-slate-800 transition-all group"
            data-testid="v2-resources-cta"
          >
            Book a call
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default V2Resources;
